// ./src/utils/api/MembershipTypeService.ts
//
// MembershipType service — the perk TEMPLATE that a Membership is an instance
// of. OpResult-native (errors normalized in ApiService). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Enum option constants (for the form selects)
// =============================================================================

export const MEMBER_SCOPE_VALUES = ["individual", "household", "company"] as const;
export type MemberScope = (typeof MEMBER_SCOPE_VALUES)[number];

export const PRICING_MODEL_VALUES = ["one_time", "subscription"] as const;
export type PricingModel = (typeof PRICING_MODEL_VALUES)[number];

export const BILLING_PERIOD_VALUES = ["monthly", "quarterly", "yearly"] as const;
export type BillingPeriod = (typeof BILLING_PERIOD_VALUES)[number];

// =============================================================================
// GraphQL
// =============================================================================

const MEMBERSHIP_TYPE_FIELDS = `
  id
  accountId
  organizerProfileId
  name
  description
  color
  numberPrefix
  isExternal
  memberScope
  maxMembers
  pricingModel
  billingPeriod
  price
  currencyCode
  durationMonths
  maxUsageCount
  isForSale
  saleDescription
  feeMode
  status
  rights
  createdAt
  updatedAt
`;

const MEMBERSHIP_TYPE_LIST_QUERY = gql`
  query MembershipTypeList($filter: MembershipTypeFilter, $params: PaginationAndSorting) {
    membershipTypeList(filter: $filter, params: $params) {
      code
      data { ${MEMBERSHIP_TYPE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MEMBERSHIP_TYPE_GET_QUERY = gql`
  query MembershipTypeGet($id: ID) {
    membershipTypeGet(id: $id) {
      code
      data { ${MEMBERSHIP_TYPE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MEMBERSHIP_TYPE_GET_MANY_QUERY = gql`
  query MembershipTypeGetMany($ids: [ID]) {
    membershipTypeGetMany(ids: $ids) {
      code
      data { ${MEMBERSHIP_TYPE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MEMBERSHIP_TYPE_CREATE_MUTATION = gql`
  mutation MembershipTypeCreate($params: MembershipTypeInput) {
    membershipTypeCreate(params: $params) {
      code
      data { ${MEMBERSHIP_TYPE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MEMBERSHIP_TYPE_UPDATE_MUTATION = gql`
  mutation MembershipTypeUpdate($where: MembershipTypeWhereInput, $params: MembershipTypeInput) {
    membershipTypeUpdate(where: $where, params: $params) {
      code
      data { ${MEMBERSHIP_TYPE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MEMBERSHIP_TYPE_REMOVE_MUTATION = gql`
  mutation MembershipTypeRemove($where: MembershipTypeWhereInput) {
    membershipTypeRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api MembershipType schema)
// =============================================================================

export interface MembershipType {
  id?: string;
  accountId?: string;
  organizerProfileId?: string | null;
  name?: string;
  description?: string | null;
  color?: string | null;
  numberPrefix?: string | null;
  isExternal?: boolean | null;
  memberScope?: string | null;
  maxMembers?: number | null;
  pricingModel?: string | null;
  billingPeriod?: string | null;
  price?: number | null;
  currencyCode?: string | null;
  durationMonths?: number | null;
  maxUsageCount?: number | null;
  /** #281 — offered on the organizer page (a one-time type with a price). */
  isForSale?: boolean | null;
  /** #281 — what the buyer reads on the page. */
  saleDescription?: string | null;
  /** #281 — who pays the fees: absorb | service | service_processor. */
  feeMode?: string | null;
  status?: number;
  /** Actions the current caller may perform on this record ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface MembershipTypeInput {
  name?: string | null;
  organizerProfileId?: string | null;
  description?: string | null;
  color?: string | null;
  numberPrefix?: string | null;
  isExternal?: boolean | null;
  memberScope?: string | null;
  maxMembers?: number | null;
  pricingModel?: string | null;
  billingPeriod?: string | null;
  price?: number | null;
  currencyCode?: string | null;
  durationMonths?: number | null;
  maxUsageCount?: number | null;
  isForSale?: boolean | null;
  saleDescription?: string | null;
  feeMode?: string | null;
  status?: number | null;
}

export interface MembershipTypeFilter {
  id?: string[] | null;
  organizerProfileId?: string[] | null;
  memberScope?: string[] | null;
  pricingModel?: string[] | null;
  searchKeyword?: string | null;
}

export interface MembershipTypeWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// A bulk update (>1 id) may only mass-change these fields server-side.
export interface MembershipTypeBulkInput {
  status?: number | null;
  durationMonths?: number | null;
  maxUsageCount?: number | null;
}

// =============================================================================
// Service
// =============================================================================

export class MembershipTypeService {
  static async list(
    filter?: MembershipTypeFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(MEMBERSHIP_TYPE_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(MEMBERSHIP_TYPE_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(MEMBERSHIP_TYPE_GET_MANY_QUERY, { ids });
  }

  static async create(params: MembershipTypeInput): Promise<OpResult> {
    return ApiService.executeMutation(MEMBERSHIP_TYPE_CREATE_MUTATION, { params });
  }

  static async update(
    where: MembershipTypeWhereInput,
    params: MembershipTypeInput
  ): Promise<OpResult> {
    return ApiService.executeMutation(MEMBERSHIP_TYPE_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(MEMBERSHIP_TYPE_REMOVE_MUTATION, { where: { id } });
  }

  /**
   * Mass-changes `status`, `durationMonths`, and/or `maxUsageCount` on every
   * selected type in one call (the server restricts a multi-id update to this
   * field subset). Pass only the fields to change.
   */
  static async bulkUpdate(ids: string[], params: MembershipTypeBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(MEMBERSHIP_TYPE_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected type in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(MEMBERSHIP_TYPE_REMOVE_MUTATION, { where: { id: ids } });
  }
}
